import { Avatars } from "@/src/components/Avatars";
import { BackButton } from "@/src/components/BackButton";
import {
  DealGroup,
  DealGroupQuery,
  DealGroupType,
  DealInviteEmailStatus,
  DealRoleType,
  useDealGroupQuery,
  useDealInviteQuery,
  useDeleteDealInviteMutation,
  useResendDealInviteMutation,
} from "@/src/graphql/generated";
import useGqlClient from "@/src/hooks/useGqlClient";
import { useQueryClient, UseQueryResult } from "@tanstack/react-query";
import { useHistory, useParams } from "react-router-dom";
import { DataRoomPermissions } from "./AccessList";
import { Avatar } from "@/src/components/account/Avatar";
import { formatDistanceToNow, fromUnixTime } from "date-fns";
import {
  BriefcaseIcon,
  CopyIcon,
  MailCheckIcon,
  MailIcon,
  PencilIcon,
  PlusIcon,
  SearchIcon,
  SendHorizonalIcon,
  ShieldUserIcon,
  TrashIcon,
} from "lucide-react";
import { Button } from "@/src/components/tailwind/Button";
import { useState } from "react";
import { InviteMoreToGroup } from "./InviteMoreToGroup";
import { EditDealGroup } from "./EditDealGroup";
import { PermissionCheck } from "@/src/components/permissions/PermissionCheck";
import { Tag } from "@/src/components/Tag";
import { useSelector } from "react-redux";
import { authSelectors } from "@/src/store/auth/selector";
import { AddPeopleFromFirmModal } from "./AddPeopleFromFirm";
import { usePermissionCheck } from "@/src/hooks/usePermissionCheck";
import clsx from "clsx";
import { TripleDotMenu } from "@/src/components/TripleDotMenu";
import { toasts } from "@/src/components/toasts/toasts";
import { MenuItem } from "@headlessui/react";
import { DealRoleName } from "@/src/components/permissions/DealRoleName";
import { EditDealInvitePermissions } from "./EditDealInvitePermissions";

export function Group() {
  const client = useGqlClient();
  const queryClient = useQueryClient();
  const { dealGroupId, dealId } = useParams<{
    dealGroupId: string;
    dealId: string;
  }>();
  const dealGroup = useDealGroupQuery(client, {
    id: dealGroupId,
    dealId,
  });
  const [openModal, setOpenModal] = useState<
    "invite_more" | "edit_group" | "add_people_from_firm" | ""
  >("");

  return (
    <div>
      <Header query={dealGroup} setOpenModal={setOpenModal} />
      <Content
        query={dealGroup}
        openModal={openModal}
        setOpenModal={setOpenModal}
      />
      {dealGroup.data && (
        <>
          <InviteMoreToGroup
            dealId={dealId}
            isOpen={openModal === "invite_more"}
            onClose={() => {
              queryClient.invalidateQueries({
                queryKey: [
                  "DealGroup",
                  { dealId, id: dealGroup.data.dealGroup.id },
                ],
              });
              setOpenModal("");
            }}
            dealGroup={dealGroup.data.dealGroup as DealGroup}
            roleTypes={
              dealGroup.data.dealGroup.type === DealGroupType.Owner
                ? [DealRoleType.Internal]
                : [DealRoleType.Guest]
            }
          />
          <EditDealGroup
            dealId={dealId}
            dealGroup={dealGroup.data.dealGroup}
            onClose={() => {
              setOpenModal("");
            }}
            open={openModal === "edit_group"}
            permissions={dealGroup.data.deal.dataRoom.permissions}
          />
          <AddPeopleFromFirmModal
            dealId={dealId}
            onClose={() => {
              setOpenModal("");
              queryClient.invalidateQueries({
                queryKey: ["DealGroup", { dealId: dealId, id: dealGroupId }],
              });
            }}
            open={openModal === "add_people_from_firm"}
          />
        </>
      )}
    </div>
  );
}

function Header(props: {
  query: UseQueryResult<DealGroupQuery, unknown>;
  setOpenModal: (modal: "invite_more" | "edit_group" | "") => void;
}) {
  if (props.query.error) {
    return (
      <div className="border-y border-concrete-100 p-4 flex items-center">
        <div className="flex flex-1 items-center gap-x-2">
          <PermissionCheck
            input={{
              roleType: DealRoleType.Internal,
              permissionKey: "access:has_access",
            }}
          >
            <BackButton size="sm" />
          </PermissionCheck>
          <p className="text-persian-990">Something went wrong</p>
        </div>
      </div>
    );
  }

  if (props.query.isLoading || !props.query.data) {
    return (
      <div className="border-y border-concrete-100 p-4 flex items-center">
        <div className="flex flex-1 items-center gap-x-2">
          <PermissionCheck
            input={{
              roleType: DealRoleType.Internal,
              permissionKey: "access:has_access",
            }}
          >
            <BackButton size="sm" />
          </PermissionCheck>
          <div className="animate-pulse">
            <div className="w-80 bg-gray-200 rounded h-8"></div>
          </div>
        </div>
      </div>
    );
  }

  const dealGroup = props.query.data.dealGroup;

  return (
    <div className="border-y border-concrete-100 p-4 flex items-center">
      <div className="flex flex-1 items-center gap-x-2">
        <PermissionCheck
          input={{
            roleType: DealRoleType.Internal,
            permissionKey: "access:has_access",
          }}
        >
          <BackButton size="sm" />
          <p className="text-persian-990 font-light">
            Access / <span className="font-semibold">{dealGroup.name}</span>
          </p>
        </PermissionCheck>
        <PermissionCheck
          input={{
            roleType: DealRoleType.Guest,
            permissionKey: "guest_access:has_access",
          }}
        >
          <p className="font-semibold text-persian-990">{dealGroup.name}</p>
        </PermissionCheck>

        <Avatars
          accounts={dealGroup.accounts.map((da) => da.account)}
          max={6}
          size="m"
          showAdd={true}
          onClick={() => {
            props.setOpenModal("invite_more");
          }}
        />

        {dealGroup.type === DealGroupType.Owner ? (
          <div className="relative group flex items-center gap-x-1.5 py-1 px-2 rounded-full border border-yellow-500 bg-yellow-100 hover:bg-yellow-200 hover:border-yellow-700 text-yellow-900">
            <ShieldUserIcon className="w-4 h-4" />
            <p className="text-sm font-semibold">Admin group</p>
            <div className="absolute left-1/2 top-full mt-2 z-10 hidden group-hover:block">
              <div className="bg-concrete-950 z-50 text-white text-xs rounded px-2 py-1 shadow-md whitespace-nowrap">
                All users in this group have full access to the Data Room and
                Questions
              </div>
            </div>
          </div>
        ) : null}
      </div>

      <PermissionCheck
        input={{
          roleType: DealRoleType.Internal,
          permissionKey: "access:edit",
        }}
      >
        <div className="flex items-center gap-x-2">
          {dealGroup.type === DealGroupType.Owner ? (
            <>
              <p className="font-semibold text-sm text-persian-990">
                Access to
              </p>
              <Tag color="blue" text="All documents" />
            </>
          ) : (
            <DataRoomPermissions
              restrictedAccess={dealGroup.overrideDataRoomAccess}
              granularAccess={dealGroup.granularAccess}
              permissions={dealGroup.dataRoomPermissions}
            />
          )}
        </div>
      </PermissionCheck>
    </div>
  );
}

function Content(props: {
  query: UseQueryResult<DealGroupQuery, unknown>;
  openModal: "invite_more" | "edit_group" | "add_people_from_firm" | "";
  setOpenModal: (
    modal: "invite_more" | "edit_group" | "add_people_from_firm" | "",
  ) => void;
}) {
  const history = useHistory();
  const { dealId } = useParams<{ dealId: string }>();
  const account = useSelector(authSelectors.account);

  const isAdmin = usePermissionCheck({
    permissionKey: "access:edit",
    roleType: DealRoleType.Internal,
  });

  if (props.query.error) {
    return (
      <div className="p-4">
        <p className="text-red-600">Failed to load group members</p>
      </div>
    );
  }

  if (props.query.isLoading || !props.query.data) {
    return (
      <div className="p-4">
        <div className="animate-pulse space-y-4">
          <div className="h-12 bg-gray-200 rounded"></div>
          <div className="h-12 bg-gray-200 rounded"></div>
          <div className="h-12 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  const dealGroup = props.query.data.dealGroup;
  const accounts = dealGroup.accounts;
  const invites = dealGroup.invites;

  return (
    <div>
      <div className="flex items-center justify-between py-2 px-3 border-b border-concrete-100">
        <div className="relative w-60 ">
          <span className="absolute inset-y-0 left-0 flex items-center pl-3">
            <SearchIcon className="h-4 w-4 text-concrete-300" />
          </span>
          <input
            type="text"
            placeholder="Search users..."
            className="placeholder:text-concrete-400 block w-full pl-10 pr-3 py-1 border border-concrete-100 rounded-md focus:outline-none focus:ring-1 focus:ring-concrete-500 focus:border-concrete-700 sm:text-sm"
          />
        </div>

        <div className="flex items-center gap-x-2">
          <PermissionCheck
            input={{
              roleType: DealRoleType.Internal,
              permissionKey: "access:edit",
            }}
          >
            <Button
              variant="secondary"
              text="Edit group"
              icon={PencilIcon}
              size="s"
              onClick={() => {
                props.setOpenModal("edit_group");
              }}
            />
            {dealGroup.type === DealGroupType.Owner &&
              account &&
              account.firm ? (
              <Button
                variant="secondary"
                text={`Add from ${account.firm.name}`}
                icon={BriefcaseIcon}
                size="s"
                onClick={() => {
                  props.setOpenModal("add_people_from_firm");
                }}
              />
            ) : null}
          </PermissionCheck>
          <Button
            variant="positive"
            text="Invite new user"
            icon={PlusIcon}
            size="s"
            onClick={() => {
              props.setOpenModal("invite_more");
            }}
          />
        </div>
      </div>
      <div className="max-h-[calc(100vh-260px)] overflow-y-scroll">
        <table className="w-full divide-y rounded-b-md divide-concrete-100">
          <thead className="">
            <tr>
              {["Name", "Role", "Joined deal"].map((t) => {
                return (
                  <th
                    scope="col"
                    className="px-3 py-2 text-left text-sm font-semibold text-persian-990"
                  >
                    {t}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-concrete-100 bg-white">
            {accounts.map((account, idx) => (
              <tr
                key={account.id}
                className={clsx(
                  "group ",
                  isAdmin ? "hover:bg-gray-50 cursor-pointer" : "",
                )}
                onClick={() => {
                  if (!isAdmin) {
                    return;
                  }

                  history.push(`/deal/${dealId}/access/account/${account.id}/`);
                }}
              >
                <td
                  className={`whitespace-nowrap px-3 py-2 ${idx === accounts.length - 1 ? "rounded-bl-md" : ""
                    }`}
                >
                  <div className="flex items-center gap-x-3">
                    <Avatar account={account.account} size="s" />
                    <span className="text-sm font-medium text-concrete-950">
                      {account.account.name}
                    </span>
                  </div>
                </td>
                <td className="px-3 py-2 text-sm text-persian-990 align-top max-w-xs">
                  <DealRoleName
                    name={account.dealRole.name}
                    permissions={account.dealRole.permissionLevels}
                    roleType={account.dealRole.type}
                  />
                </td>
                {/* <td className="whitespace-nowrap px-3 py-2 text-sm text-persian-990">
                  <DataRoomPermissions
                    permissions={account.dataRoomPermissions}
                  />
                </td> */}
                <td
                  className={`whitespace-nowrap px-3 py-2 text-sm text-persian-990 ${idx === accounts.length - 1 ? "rounded-br-md" : ""
                    }`}
                >
                  {formatDistanceToNow(fromUnixTime(account.createdAt), {
                    addSuffix: true,
                  })}
                </td>
              </tr>
            ))}
            {invites.map((inv, idx) => {
              return (
                <DealInviteRow
                  id={inv.id}
                  isLast={idx === invites.length - 1}
                />
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DealInviteRow(props: { id: string; isLast: boolean }) {
  const client = useGqlClient();
  const queryClient = useQueryClient();
  const [editPermissionsOpen, setEditPermissionsOpen] = useState(false);
  const dealInviteQuery = useDealInviteQuery(
    client,
    {
      id: props.id,
    },
    {
      refetchInterval(query) {
        if (
          query.state.data &&
          query.state.data.dealInvite.emailStatus !== DealInviteEmailStatus.Sent
        ) {
          return 1000;
        }

        return false;
      },
    },
  );

  const resendInvite = useResendDealInviteMutation(client);
  const deleteInvite = useDeleteDealInviteMutation(client);
  const { dealGroupId } = useParams<{ dealId: string; dealGroupId: string }>();

  if (dealInviteQuery.error) {
    return (
      <tr key={props.id} className="group hover:bg-gray-50 cursor-pointer">
        <td
          className={`whitespace-nowrap px-3 py-2 ${props.isLast ? "rounded-bl-md" : ""
            }`}
        >
          <div className="flex items-center gap-x-3">
            <span className="text-sm font-medium text-red-700">
              Failed to load invite
            </span>
          </div>
        </td>
        <td className="whitespace-nowrap px-3 py-2 text-sm text-persian-990"></td>

        <td
          className={` whitespace-nowrap px-3 py-2 text-sm text-persian-990 ${props.isLast ? "rounded-br-md" : ""
            }`}
        ></td>
      </tr>
    );
  }

  if (dealInviteQuery.isLoading || !dealInviteQuery.data) {
    return (
      <tr key={props.id} className="group hover:bg-gray-50 cursor-pointer">
        <td
          className={`whitespace-nowrap px-3 py-2 ${props.isLast ? "rounded-bl-md" : ""
            }`}
        >
          <div className="flex items-center gap-x-3">
            <div className="animate-pulse">
              <div className="w-80 bg-gray-200 rounded h-4"></div>
            </div>
          </div>
        </td>
        <td className="whitespace-nowrap px-3 py-2 text-sm text-persian-990">
          <div className="animate-pulse">
            <div className="w-12 bg-gray-200 rounded h-4"></div>
          </div>
        </td>

        <td
          className={` whitespace-nowrap px-3 py-2 text-sm text-persian-990 ${props.isLast ? "rounded-br-md" : ""
            }`}
        >
          <div className="flex items-center gap-x-1.5">
            <div className="animate-pulse">
              <div className="w-32 bg-gray-200 rounded h-4"></div>
            </div>
          </div>
        </td>
      </tr>
    );
  }

  const inv = dealInviteQuery.data.dealInvite;

  return (
    <>
      <tr key={props.id} className="group hover:bg-gray-50 cursor-pointer">
        <td
          className={`whitespace-nowrap px-3 py-2 ${props.isLast ? "rounded-bl-md" : ""
            }`}
        >
          <div className="flex items-center gap-x-3">
            <span className="text-sm font-medium text-concrete-950">
              {inv.email}
            </span>
          </div>
        </td>
        <td className="px-3 py-2 text-sm text-persian-990 align-top max-w-xs">
          <DealRoleName
            name={inv.dealRole.name}
            permissions={inv.dealRole.permissionLevels}
            roleType={inv.dealRole.type}
          />
        </td>

        <td
          className={` whitespace-nowrap px-3 py-2 text-sm text-persian-990 ${props.isLast ? "rounded-br-md" : ""
            }`}
        >
          <div className="flex items-center gap-x-1.5">
            {inv.emailStatus === DealInviteEmailStatus.Sending ? (
              <>
                <MailIcon className="w-3 h-3 " />
                <p>Sending invite...</p>
              </>
            ) : (
              <>
                <MailCheckIcon className="w-3 h-3 " />
                <p>Invite sent</p>
              </>
            )}

            <TripleDotMenu width="w-40">
              <MenuItem>
                <TripleDotMenu.Button
                  text="Edit role"
                  onClick={() => setEditPermissionsOpen(true)}
                  icon={<PencilIcon className="w-4 h-4" />}
                />
              </MenuItem>
              <MenuItem>
                <TripleDotMenu.Button
                  text="Resend invite"
                  onClick={() => {
                    resendInvite.mutate(
                      {
                        id: props.id,
                      },
                      {
                        onSuccess: () => {
                          toasts.success("Resending invite...");

                          queryClient.invalidateQueries({
                            queryKey: ["DealGroup", { id: dealGroupId }],
                          });

                          setTimeout(() => {
                            queryClient.invalidateQueries({
                              queryKey: ["DealInvite", { id: props.id }],
                            });
                          }, 1000);
                        },
                      },
                    );
                  }}
                  icon={<SendHorizonalIcon className="w-4 h-4" />}
                />
              </MenuItem>
              <TripleDotMenu.Button
                text="Copy invite link"
                onClick={() => {
                  const rootDomain = window.location.origin;
                  const inviteLink = `${rootDomain}/deal-invite/${inv.token}`;
                  navigator.clipboard.writeText(inviteLink).then(() => {
                    toasts.success("Invite link copied to clipboard");
                  });

                }}
                icon={<CopyIcon className="w-4 h-4" />}
              />
              <MenuItem>
                <TripleDotMenu.Button
                  text="Delete invite"
                  onClick={() => {
                    deleteInvite.mutate(
                      {
                        id: props.id,
                      },
                      {
                        onSuccess: () => {
                          toasts.success("Invite deleted");
                          queryClient.invalidateQueries({
                            queryKey: ["DealGroup", { id: dealGroupId }],
                          });
                        },
                      },
                    );
                  }}
                  variant="danger"
                  icon={<TrashIcon className="w-4 h-4" />}
                />
              </MenuItem>
            </TripleDotMenu>
          </div>
        </td>
      </tr>
      <EditDealInvitePermissions
        invite={inv}
        open={editPermissionsOpen}
        onClose={() => setEditPermissionsOpen(false)}
      />
    </>
  );
}
